import React, { useCallback, useRef, useState } from 'react';
import useSWR from 'swr';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { isAxiosError } from 'axios';
import type { FileRejection } from 'react-dropzone';
import { ConfirmDialog, ErrorMessage, LoadingSpinner } from '@/shared/components';
import { FileUploadDropzone } from '@/shared/ui';
import { galleryApi, type GalleryImage } from '@/shared/api';
import { useNotificationActions } from '@/shared/stores/notification-store';
import { DraggableGalleryTile } from './DraggableGalleryTile';
import { shrinkImage } from './shrinkImage';

/**
 * Предел зоны загрузки. Больше, чем пропускает сервер (10 МБ): большие фото
 * уменьшаются в браузере, а файл сверх предела зона отбросила бы молча.
 */
const DROPZONE_MAX_SIZE = 50 * 1024 * 1024;

interface FailedFile {
  name: string;
  reason: string;
}

/** Текст ошибки для заказчика: сообщение сервера, если оно есть и по-русски. */
const describeError = (error: unknown): string => {
  if (isAxiosError<{ message?: unknown }>(error)) {
    if (error.code === 'ECONNABORTED') {
      return 'Истекло время ожидания — проверьте связь и попробуйте ещё раз';
    }
    if (!error.response) {
      return 'Нет связи с сервером';
    }
    if (error.response.status === 413) {
      return 'Файл слишком большой';
    }
    // Ответы 5xx приходят с английским «Internal server error».
    if (error.response.status >= 500) {
      return 'Ошибка на сервере — попробуйте ещё раз';
    }
    const message = error.response.data?.message;
    if (typeof message === 'string') {
      return message;
    }
  }
  return error instanceof Error ? error.message : 'Неизвестная ошибка';
};

const describeRejection = ({ errors }: FileRejection): string => {
  const codes = errors.map(({ code }) => code);
  if (codes.includes('file-too-large')) {
    return 'Файл больше 50 МБ';
  }
  if (codes.includes('file-invalid-type')) {
    return 'Это не JPG, PNG или WebP';
  }
  return errors[0]?.message ?? 'Файл не подошёл';
};

/** Тот же ли порядок — сравниваются только id по позициям. */
const isSameOrder = (a: readonly GalleryImage[], b: readonly GalleryImage[]): boolean =>
  a.length === b.length && a.every(({ id }, index) => id === b[index]?.id);

/**
 * Лента фотографий «О клинике» внизу главной: загрузка пачкой, порядок
 * перетаскиванием, удаление. Изменения видны на сайте сразу — главная
 * собирается на сервере при каждом показе.
 */
export const GalleryManager: React.FC = () => {
  const { showSuccess, showError } = useNotificationActions();
  const { data, error, isLoading, mutate } = useSWR('admin-gallery', () => galleryApi.list(), {
    revalidateOnFocus: false
  });
  const saved = data?.images;

  /*
    Порядок, пока плитку тянут и пока он сохраняется. В остальное время сетка
    показывает то, что пришло с сервера, и не расходится с сайтом. Ref
    повторяет состояние для обработчика отпускания: тот срабатывает в одном
    событии с последним наведением — раньше, чем React перерисует.
  */
  const [draft, setDraft] = useState<GalleryImage[] | null>(null);
  const draftRef = useRef<GalleryImage[] | null>(null);
  const shown = draft ?? saved ?? [];

  const [progress, setProgress] = useState<{ current: number; total: number } | null>(null);
  const [failed, setFailed] = useState<FailedFile[]>([]);
  const [isSavingOrder, setIsSavingOrder] = useState(false);
  const [toDelete, setToDelete] = useState<GalleryImage | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const isBusy = progress !== null || isSavingOrder;

  /**
   * Файлы уходят по одному и с конца: каждое новое фото встаёт в начало
   * ленты, поэтому так пачка ляжет в том порядке, в каком её выбрали.
   * Сбой одного файла не останавливает остальные.
   */
  const handleFiles = async (files: File[]): Promise<void> => {
    setFailed([]);
    const queue = [...files].reverse();
    const problems: FailedFile[] = [];

    for (const [position, file] of queue.entries()) {
      setProgress({ current: position + 1, total: queue.length });
      try {
        await galleryApi.upload(await shrinkImage(file));
        await mutate();
      } catch (uploadError) {
        problems.unshift({ name: file.name, reason: describeError(uploadError) });
      }
    }

    setProgress(null);
    // Отклонённые зоной загрузки файлы этой же пачки уже в списке — дописываем.
    setFailed((previous) => [...previous, ...problems]);

    const uploaded = queue.length - problems.length;
    if (uploaded > 0) {
      showSuccess(`Загружено фотографий: ${uploaded}`);
    }
    if (problems.length > 0) {
      showError(`Не загрузились: ${problems.length}`);
    }
  };

  const handleRejected = (rejections: FileRejection[]): void => {
    setFailed(
      rejections.map((rejection) => ({
        name: rejection.file.name,
        reason: describeRejection(rejection)
      }))
    );
  };

  const handleMove = useCallback(
    (dragIndex: number, hoverIndex: number): void => {
      const next = [...(draftRef.current ?? saved ?? [])];
      const [moved] = next.splice(dragIndex, 1);
      if (!moved) {
        return;
      }
      next.splice(hoverIndex, 0, moved);
      draftRef.current = next;
      setDraft(next);
    },
    [saved]
  );

  /**
   * Порядок сохраняется при любом отпускании — на плитке, в просвете между
   * плитками, на пустых клетках после последней: иначе сетка показала бы
   * порядок, которого нет ни в базе, ни на сайте. Ничего не сдвинули — нет и
   * запроса.
   */
  const handleDragEnd = useCallback(async (): Promise<void> => {
    const final = draftRef.current;
    draftRef.current = null;

    if (!final || !saved || isSameOrder(final, saved)) {
      setDraft(null);
      return;
    }

    setIsSavingOrder(true);
    try {
      const response = await galleryApi.updateSortOrder(
        final.map(({ id }, index) => ({ id, sortOrder: index + 1 }))
      );
      await mutate(response, { revalidate: false });
      showSuccess('Порядок сохранён');
    } catch (saveError) {
      showError(describeError(saveError));
      await mutate();
    } finally {
      setDraft(null);
      setIsSavingOrder(false);
    }
  }, [saved, mutate, showSuccess, showError]);

  const handleDelete = async (): Promise<void> => {
    if (!toDelete) {
      return;
    }
    setIsDeleting(true);
    try {
      await galleryApi.remove(toDelete.id);
      await mutate();
      showSuccess('Фотография удалена');
    } catch (deleteError) {
      showError(describeError(deleteError));
    } finally {
      setIsDeleting(false);
      setToDelete(null);
    }
  };

  return (
    <div className="card card-bordered bg-base-100 shadow-sm">
      <div className="card-body p-6">
        <h3 className="text-lg font-semibold text-base-content mb-1">
          <i className="fas fa-images mr-2 text-primary"></i>
          Фотографии клиники
        </h3>
        <p className="text-sm text-base-content/60 mb-4">
          Лента внизу главной страницы, в блоке «О клинике». Порядок здесь тот же, что на
          сайте, — слева направо. Перетащите фото, чтобы поменять порядок; новые встают в
          начало.
        </p>

        {isLoading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner size="lg" text="Загрузка фотографий..." />
          </div>
        ) : error && !saved ? (
          // Сбой фонового обновления при уже показанной ленте сетку не прячет.
          <ErrorMessage message="Не удалось загрузить фотографии. Обновите страницу." />
        ) : shown.length === 0 ? (
          <p className="text-base-content/60 mb-4">
            Фотографий пока нет — на сайте лента не показывается.
          </p>
        ) : (
          <DndProvider backend={HTML5Backend}>
            <ul className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3 mb-4 list-none p-0">
              {shown.map((image, index) => (
                <DraggableGalleryTile
                  key={image.id}
                  image={image}
                  index={index}
                  disabled={isBusy}
                  onMove={handleMove}
                  onDragEnd={handleDragEnd}
                  onDelete={setToDelete}
                />
              ))}
            </ul>
          </DndProvider>
        )}

        <FileUploadDropzone
          onFileSelect={handleFiles}
          onFilesRejected={handleRejected}
          multiple
          maxSize={DROPZONE_MAX_SIZE}
          disabled={isBusy}
          dropzoneText="Перетащите фотографии сюда или нажмите, чтобы выбрать"
          helpText="JPG, PNG или WebP, можно сразу много. Большие фото с фотосессии уменьшаются автоматически."
        />

        {progress && (
          <div className="flex items-center justify-center gap-2 mt-4 p-3 bg-primary/10 rounded-lg">
            <LoadingSpinner size="sm" />
            <span className="text-sm text-primary font-medium">
              Загружается фото {progress.current} из {progress.total}
            </span>
          </div>
        )}

        {failed.length > 0 && (
          <div className="alert alert-warning mt-4 flex-col items-start">
            <span className="font-medium">Не загрузились:</span>
            <ul className="list-disc ml-5">
              {failed.map(({ name, reason }, index) => (
                <li key={index}>
                  {name} — {reason}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={toDelete !== null}
        title="Удалить фотографию"
        message="Фото пропадёт из ленты на сайте. Вернуть его можно, только загрузив заново."
        confirmText="Удалить"
        cancelText="Отмена"
        onConfirm={handleDelete}
        onCancel={() => setToDelete(null)}
        isLoading={isDeleting}
      />
    </div>
  );
};
